import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  TextInput,
  SimpleGrid,
  ActionIcon,
  Stack,
  LoadingOverlay,
  Center,
  SegmentedControl,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconUpload,
  IconPhoto,
  IconCalendar,
  IconClock,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";
import { uploadFetch } from "../../utils/directUpload";
import { useNotification } from "../adminProjectNotifications/popupnotifications"; // Adjust path if needed

const BASE_URL = `${API_BASE_URL}`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export default function MegaQuizBannerConfig() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    bannerEnTitle: "",
    bannerJaTitle: "",
    imageUrl: "",
  });
  const [previewLang, setPreviewLang] = useState("en");
  const [quizDates, setQuizDates] = useState({ start: null, end: null });

  const formatPreviewDate = (dateString) => {
    if (!dateString) return "TBD";
    const d = new Date(dateString);
    const month = d.toLocaleString("en-US", { month: "long" });
    const day = d.getDate();
    const weekday = d.toLocaleString("en-US", { weekday: "short" });
    return `${month} ${day} (${weekday})`;
  };

  const formatPreviewTime = (startString, endString) => {
    if (!startString || !endString) return "TBD";
    const formatTime = (ds) => {
      const d = new Date(ds);
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };
    return `${formatTime(startString)} - ${formatTime(endString)}`;
  };
  useEffect(() => {
    const fetchPreviewData = async () => {
      setLoading(true);
      try {
        const bannerRes = await fetch(
          `${BASE_URL}/megaquiz/getMegaQuizBannerforadmin`,
          {
            headers: getAuthHeaders(),
          },
        );
        const bannerData = await bannerRes.json();

        if (bannerData.success && bannerData.data) {
          setFormData({
            bannerEnTitle: bannerData.data.bannerEnTitle || "",
            bannerJaTitle: bannerData.data.bannerJaTitle || "",
            imageUrl: bannerData.data.imageUrl || "",
          });
        }

        const quizRes = await fetch(
          `${BASE_URL}/megaquiz/getmegaquiz?lang=en`,
          {
            headers: getAuthHeaders(),
          },
        );
        const quizData = await quizRes.json();

        if (quizData && quizData.startTime) {
          setQuizDates({
            start: quizData.startTime,
            end: quizData.endTime,
          });
        }
      } catch (error) {
        console.error("Failed to load preview data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPreviewData();
  }, []);

  // --- IMAGE UPLOAD LOGIC ---
  const handleImageDrop = async (files) => {
    setUploadingImage(true);
    try {
      const file = files[0];
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = async () => {
        const imagePayload = [{ image: reader.result, description: file.name }];

        const response = await uploadFetch(`${BASE_URL}/auth/bulkUploadImages`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ images: imagePayload }),
        });

        const data = await response.json();
        if (data.results && data.results[0]?.success) {
          setFormData((prev) => ({ ...prev, imageUrl: data.results[0].url }));
          showSuccess("Success", "Image uploaded successfully");
        } else {
          showError("Upload Failed", "Could not upload image to server.");
        }
        setUploadingImage(false);
      };

      reader.onerror = () => {
        showError("File Error", "Failed to read the file.");
        setUploadingImage(false);
      };
    } catch (error) {
      showError("Network Error", "Failed to communicate with image server.");
      setUploadingImage(false);
    }
  };

  // --- SUBMIT LOGIC ---
  const handleSave = async () => {
    if (
      !formData.imageUrl &&
      (!formData.bannerEnTitle || !formData.bannerJaTitle)
    ) {
      showError("Validation", "Please provide titles or an image to update.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/megaquiz/loadMegaQuizBanner`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        showSuccess("Saved", "Mega Quiz Banner successfully updated!");
        navigate("/admin/dashboard/mega-quiz-management");
      } else {
        showError("Error", data.error || "Failed to update banner.");
      }
    } catch (e) {
      showError("Network Error", "Failed to save banner data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW={1000} mx="auto" pb={50} pos="relative">
      <LoadingOverlay visible={loading} />

      {/* HEADER */}
      <Group justify="space-between" mb="xl">
        <Group>
          <ActionIcon variant="light" size="lg" onClick={() => navigate(-1)}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Box>
            <Text size="xl" fw={800} className="ota-gradient-text">
              Mega Quiz Banner Editor
            </Text>
            <Text size="sm" c="dimmed">
              Design the promotional banner shown to users
            </Text>
          </Box>
        </Group>
        <Button
          leftSection={<IconDeviceFloppy size={18} />}
          onClick={handleSave}
          className="btn-ota-primary"
        >
          Save Banner
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
        {/* LEFT: EDITING FORM */}
        <Stack>
          <Paper p="xl" radius="md" withBorder>
            <Text fw={700} mb="md">
              Banner Text
            </Text>
            <Stack>
              <TextInput
                label="English Title"
                placeholder="e.g. Win Big This Weekend!"
                value={formData.bannerEnTitle}
                onChange={(e) =>
                  setFormData({ ...formData, bannerEnTitle: e.target.value })
                }
              />
              <TextInput
                label="Japanese Title"
                placeholder="e.g. 今週末は大きく勝とう！"
                value={formData.bannerJaTitle}
                onChange={(e) =>
                  setFormData({ ...formData, bannerJaTitle: e.target.value })
                }
              />
            </Stack>
          </Paper>

          <Paper p="xl" radius="md" withBorder pos="relative">
            <LoadingOverlay
              visible={uploadingImage}
              overlayProps={{ blur: 2 }}
            />
            <Group justify="space-between" mb="md">
              <Text fw={700}>Banner Background Image</Text>
              {formData.imageUrl && (
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={() => setFormData({ ...formData, imageUrl: "" })}
                >
                  Remove
                </Button>
              )}
            </Group>

            <Dropzone
              onDrop={handleImageDrop}
              accept={IMAGE_MIME_TYPE}
              multiple={false}
              style={{
                border: "2px dashed var(--mantine-color-blue-4)",
                backgroundColor: "var(--mantine-color-blue-0)",
                padding: 40,
                textAlign: "center",
              }}
            >
              <Center>
                <IconUpload size={40} color="var(--mantine-color-blue-6)" />
              </Center>
              <Text fw={600} mt="sm" c="blue.7">
                Drag & Drop banner image here
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                Recommended resolution: 360x460px (portrait)
              </Text>
            </Dropzone>
          </Paper>
        </Stack>

        {/* RIGHT: LIVE PREVIEW */}

        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Text fw={700} mb="xs">
            Live App Preview
          </Text>
          <Text size="xs" c="dimmed" mb="md" ta="center">
            This is exactly how it will appear on mobile screens.
          </Text>

          {/* THE NEW LANGUAGE SELECTOR */}
          <SegmentedControl
            value={previewLang}
            onChange={setPreviewLang}
            data={[
              { label: "English", value: "en" },
              { label: "Japanese", value: "ja" },
            ]}
            size="xs"
            mb="lg"
            color="blue"
          />

          {/* THE 180x230 EXACT PREVIEW BOX */}
          <Box
            style={{
              width: 473,
              height: 345,
              borderRadius: 16,
              overflow: "hidden",
              position: "relative",
              backgroundColor: "#0f172a",
              border: "4px solid #F59E0B",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            }}
          >
            {formData.imageUrl ? (
              <img
                src={formData.imageUrl}
                alt="Banner Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter:
                    formData.bannerEnTitle || formData.bannerJaTitle
                      ? "brightness(0.65)"
                      : "none",
                  transition: "filter 0.3s ease",
                }}
              />
            ) : (
              <Center h="100%">
                <Stack align="center" gap={5}>
                  <IconPhoto size={32} color="#475569" />
                  <Text size="xs" c="dimmed" fw={600}>
                    No Image
                  </Text>
                </Stack>
              </Center>
            )}

            {/* DYNAMIC TITLE TEXT (Centered slightly high) */}
            {(formData.bannerEnTitle || formData.bannerJaTitle) && (
              <Box
                style={{
                  position: "absolute",
                  top: "35%",
                  left: 0,
                  right: 0,
                  transform: "translateY(-50%)",
                  padding: "0 25px ",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {previewLang === "en" && formData.bannerEnTitle && (
                  <Text
                    c="white"
                    fw={900}
                    size="lg"
                    lh={1.2}
                    lineClamp={2}
                    style={{ textShadow: "0px 2px 8px rgba(0,0,0,0.9)" }}
                  >
                    {formData.bannerEnTitle}
                  </Text>
                )}
                {previewLang === "ja" && formData.bannerJaTitle && (
                  <Text
                    c="white"
                    fw={900}
                    size="lg"
                    lh={1.2}
                    lineClamp={2}
                    style={{ textShadow: "0px 2px 8px rgba(0,0,0,0.9)" }}
                  >
                    {formData.bannerJaTitle}
                  </Text>
                )}
              </Box>
            )}

            {/* THE BOTTOM APP UI (Gradient, Dates, Button) */}
            <Box
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "12px 25px",
                background:
                  "linear-gradient(to top, rgba(15,23,42,1) 0%, rgba(15,23,42,0.8) 50%, rgba(15,23,42,0) 100%)",
              }}
            >
              {/* Date & Time Row */}
              <Group gap="md" mb={8} wrap="nowrap">
                <Group gap={4}>
                  <IconCalendar size={22} color="#FBBF24" />
                  <Text
                    c="white"
                    size="s"
                    fw={600}
                    style={{ textShadow: "1px 1px 2px black" }}
                  >
                    {formatPreviewDate(quizDates.start)}
                  </Text>
                </Group>
                <Group gap={4}>
                  <IconClock size={22} color="#FBBF24" />
                  <Text
                    c="white"
                    size="s"
                    fw={600}
                    style={{ textShadow: "1px 1px 2px black" }}
                  >
                    {formatPreviewTime(quizDates.start, quizDates.end)}
                  </Text>
                </Group>
              </Group>

              {/* Play Now Button */}
              <Button
                radius="lg"
                size="lg"
                style={{
                  background: "linear-gradient(to right, #F97316, #FBBF24)", // Orange to Yellow gradient
                  border: "none",
                  color: "white",
                  fontWeight: 800,
                  boxShadow: "0 4px 14px rgba(249, 115, 22, 0.4)",
                  width: 150,
                }}
              >
                Play Now
              </Button>
            </Box>
          </Box>
        </Paper>
      </SimpleGrid>
    </Box>
  );
}
